import {
    FormAction,
    FormContext,
    FormDataResult,
    FormDataStatus,
    FormDefinition,
    SystemEntity,
    UserAction,
} from '@wdx/shared/utils';
import { ValidationSummarySection } from './form-validation.model';
import { LayoutAndDefinitionEntry } from './reactive-dynamic.model';
import { Observable } from 'rxjs';
import { SummaryInfoCard } from '../services';

export enum FormFrameworkEventType {
    Dirty,
    SaveStart,
    SaveFailed,
    SaveClicked,
    SaveDraftClicked,
    SaveSuccess,
    FormLoaded,
    FormInitialised,
    FormValueChanged,
    Touched,
    AuditUserAction,
    FormRendered,
}

export interface FormFrameworkEventAction {
    dirty?: boolean;
    pristine?: boolean;
    touched?: boolean;
    formValue?: Record<string, any>;
    formName?: string | null;
    valid?: boolean;
    event?: Event;
    formStatus?: FormDataStatus;
    entityId?: string | null;
    entityType?: SystemEntity;
    userAction?: {
        action: UserAction;
        entity: SystemEntity;
        id: string;
    };
    actions?: FormAction[] | null;
    validationSummary?: ValidationSummarySection[];
    externallyTriggered?: boolean;
    saveId?: string;
    initialFormData?: any;
    layoutAndDefinition$?: Observable<LayoutAndDefinitionEntry[]>;
    formDefinition?: FormDefinition;
    formValueChanges$?: Observable<any>;
    context?: FormContext;
    infoCards$?: Observable<SummaryInfoCard[]>;
    savedFormData?: Record<string, any>;
}

export interface FormFrameworkEvent {
    type: FormFrameworkEventType;
    typeName: string;
    action?: FormFrameworkEventAction;
}

export class FormFrameworkFullDataEvent implements FormFrameworkEvent {
    type: FormFrameworkEventType;
    typeName: string;
    action: FormFrameworkEventAction;

    constructor(
        type: FormFrameworkEventType,
        action: FormFrameworkEventAction,
    ) {
        this.type = type;
        this.typeName = FormFrameworkEventType[type];
        this.action = action;
    }
}

export class FormFrameworkFormValueChangedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.FormValueChanged, action);
    }
}

export class FormFrameworkFormTouchedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.Touched, action);
    }
}

export class FormFrameworkSaveStartEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.SaveStart, action);
    }
}

export class FormFrameworkSaveFailedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.SaveFailed, action);
    }
}

export class FormFrameworkSaveSuccessEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.SaveSuccess, action);
    }
}

export class FormFrameworkLoadedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.FormLoaded, action);
    }
}

export class FormFrameworkInitialisedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.FormInitialised, action);
    }
}

export class FormFrameworkRenderedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.FormRendered, action);
    }
}

export class FormFrameworkSaveClickedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.SaveClicked, action);
    }
}

export class FormFrameworkSaveDraftClickedEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.SaveDraftClicked, action);
    }
}

export class FormFrameworkDirtyEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.Dirty, action);
    }
}

export class FormFrameworkAuditUserActionEvent extends FormFrameworkFullDataEvent {
    constructor(action: FormFrameworkEventAction) {
        super(FormFrameworkEventType.AuditUserAction, action);
    }
}
